<!-- eslint-disable vue/no-mutating-props -->
<template>
	<div
		:class="{
			animate__animated: deleted || mediaItem._new,
			animate__zoomInUp: mediaItem._new,
			animate__zoomOutDown: deleted
		}"
		class="media-list-item ui-elevation-1 panel"
	>
		<div class="media-list-item__image-container">
			<div
				v-if="!imageLoaded"
				class="media-list-item__image-placeholder"
			>
				<i class="fas fa-image"></i>
			</div>
			<!--
				Note: setting width to 640px on img tag below forces old thumbnails (322x181)
				to show as same sizes as new in grid
			-->
			<img
				v-show="imageLoaded"
				:alt="mediaItem.comment || mediaItem.title"
				:src="`${recorderApiUrl}/media?secid=${encodedSecId}&type=image`"
				width="640px"
				@load="imageLoaded = true"
			>
			<div
				class="media-list-item__play-button"
				@click="$emit('playMedia', mediaItem, recorderApiUrl)"
			>
				<i class="fas fa-play fa-4x fa-inverse"></i>
			</div>
			<div class="media-list-item__image-top-overlay">
				<ui-button
					:style="{ visibility: mediaItem._selected ? 'visible' : '' }"
					:icon="`${mediaItem._selected ? 'fas' : 'fal'} fa-check-circle`"
					:tooltip="`${mediaItem._selected ? 'Deselect video' : 'Select video'}`"
					@click="mediaItem._selected = !mediaItem._selected;"
				/>
				<ui-menu
					icon="fal fa-ellipsis-v fa-2x"
					tooltip="Actions"
				>
					<ui-menu-item
						icon="fal fa-share"
						label="Share"
						@click="$emit('shareMedia', mediaItem)"
					/>
					<ui-menu-item
						icon="fal fa-download"
						label="Download"
						@click="downloadMedia"
					/>
					<ui-menu-item
						:disabled="mediaItem.duration === 0"
						icon="fal fa-cut"
						label="Trim"
						@click="$emit('trimMedia', mediaItem, recorderApiUrl);"
					/>
					<ui-menu-item
						icon="fal fa-trash"
						label="Delete"
						@click="$emit('confirmDeleteMedia', mediaItem)"
					/>
				</ui-menu>
			</div>
			<div class="media-list-item__image-bottom-overlay">
				<div class="ui-caption">{{ mediaItem.size | bytes }}</div>
				<div class="ui-caption">{{ mediaItem.duration | timecode }}</div>
			</div>
		</div>

		<ui-form :is-processing="isProcessing">
			<div class="media-list-item__footer">
				<div class="media-list-item__footer__main">
					<div class="media-list-item__footer__main__text">
						<div v-if="!mediaItem.comment" :title="mediaItem.title" class="ui-subheading">{{ mediaItem.title }}</div>
						<div v-if="mediaItem.comment" :title="`${mediaItem.comment} - ${mediaItem.title}`" class="ui-subheading">{{ mediaItem.comment }}</div>
						<div class="ui-caption">{{ mediaItem.created | date }}</div>
					</div>
					<div class="media-list-item__footer__main__expand-button">
						<ui-button
							v-if="!expanded"
							icon="fal fa-caret-down"
							@click="expanded = true; syncMetadata()"
						/>
						<ui-button
							v-if="expanded"
							icon="fal fa-caret-up"
							@click="expanded = false"
						/>
					</div>
				</div>
				<div v-if="expanded" class="media-list-item__footer__expanded">
					<ui-input
						v-model="comment"
						label="Title"
					/>
					<ui-text-area
						v-model="description"
						autogrow
						label="Description"
					/>
					<ui-chips
						v-model="tags"
						placeholder="Add tag"
					/>
					<ui-chips
						v-model="participants"
						placeholder="Add participant"
					/>
					<div class="media-list-item__footer__expanded__actions">
						<ui-button
							primary
							text="Save"
							tooltip="Save changes to metadata"
							@click="updateMedia"
						/>
					</div>
				</div>
			</div>
		</ui-form>
	</div>
</template>

<script>
/* eslint-disable vue/no-mutating-props */
import UiButton from "lib/components/ui/Button";
import UiChips from "lib/components/ui/Chips";
import UiForm from "lib/components/ui/Form";
import UiInput from "lib/components/ui/Input";
import UiMenu from "lib/components/ui/Menu";
import UiMenuItem from "lib/components/ui/MenuItem";
import UiTextArea from "lib/components/ui/TextArea";
import alert from "lib/modules/Alert";
import media from "lib/modules/Media";
import http from "lib/modules/Http";

export default {
	name: "MediaListItem",
	components: {
		UiButton,
		UiChips,
		UiForm,
		UiInput,
		UiMenu,
		UiMenuItem,
		UiTextArea
	},
	props: {
		mediaItem: {
			type: Object,
			required: true
		},
		recorderApiUrl: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			comment: this.mediaItem.comment,
			deleted: false,
			description: this.mediaItem.description,
			encodedSecId: encodeURIComponent(this.mediaItem.secid),
			expanded: false,
			imageLoaded: false,
			isProcessing: false,
			participants: [...this.mediaItem.participants],
			tags: [...this.mediaItem.tags]
			// see http://redmine.mns.vc/issues/378 for array spread explanation
		};
	},
	created() {
		this.enterAnimationTimer = null;
		this.leaveAnimationTimer = null;
	},
	mounted() {
		if (this.mediaItem._new)
			this.enterAnimationTimer = setTimeout(() => {
				this.mediaItem._new = false;
			}, 1000);
	},
	beforeDestroy() {
		clearTimeout(this.enterAnimationTimer);
		clearTimeout(this.leaveAnimationTimer);
	},
	methods: {
		deleteAnimationStart() {
			this.deleted = true;
			clearTimeout(this.leaveAnimationTimer);
			this.leaveAnimationTimer = setTimeout(() => {
				this.$emit("remove");
			}, 1000);
		},
		deleteAnimationEnd() {
			this.deleted = false;
			clearTimeout(this.leaveAnimationTimer);
		},
		downloadMedia() {
			const name = encodeURI(media.filename(this.mediaItem.title, this.mediaItem.created * 1000));

			location.href = `${this.recorderApiUrl}/media?secid=${this.encodedSecId}&type=video&mode=download&name=${name}`;
		},
		syncMetadata() {
			this.comment = this.mediaItem.comment;
			this.description = this.mediaItem.description;
			this.participants = [...this.mediaItem.participants];
			this.tags = [...this.mediaItem.tags];
		},
		updateMedia() {
			this.isProcessing = true;

			http.patch(`${this.recorderApiUrl}/media/${this.mediaItem.media_id}`, {
				comment: this.comment,
				description: this.description,
				participants: this.participants,
				tags: this.tags
			})
			.then(() => {
				this.expanded = false;
				this.mediaItem.comment = this.comment;
				this.mediaItem.description = this.description;
				this.mediaItem.participants = this.participants;
				this.mediaItem.tags = this.tags;
			})
			.catch((error) => {
				alert.error("Unable to update media data", error.response);
			})
			.finally(() => {
				this.isProcessing = false;
			});
		}
	}
};
</script>

<style lang="scss" scoped>
.media-list-item {
	$color-transparent-light: rgba(255, 255, 255, 0.8);
	$color-transparent-dark: rgba(0, 0, 0, 0.75);

	/deep/ button {
		z-index: 3; // needed to make all buttons in item not show on top of floating buttons or need help button
	}
	.media-list-item__image-container {
		position: relative;
		text-align: center;
		.media-list-item__image-placeholder {
			background-color: #ccc;
			position: relative;
			padding-top: 56.25%; /* 16:9 Aspect Ratio */
			width: 100%;
			i {
				position: absolute;
				margin: auto;
				top: 0;
				right: 0;
				bottom: 0;
				left: 0;
				height: 146px;
				font-size: 146px;
			}
		}
		.media-list-item__image-top-overlay {
			pointer-events: none;
			position: absolute;
			top: 0;
			padding: 8px;
			width: 100%;
			/deep/ button {
				transition: none; // has a transition with 0.4s duration by default, this makes setting visibility:hidden lag by 0.4s
				pointer-events: auto;
				background-color: $color-transparent-dark;
				i {
					color: $color-transparent-light;
				}
			}
			/deep/ .ui-menu {
				visibility: hidden;
				pointer-events: auto;
				float: right;
			}
			button:first-child {
				visibility: hidden;
				float: left;
				margin: 0;
			}
		}
		.media-list-item__play-button {
			display: flex;
			visibility: hidden;
			align-items: center;
			justify-content: center;
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			i {
				color: $color-transparent-light;
			}
		}
		.media-list-item__image-bottom-overlay {
			pointer-events: none;
			position: absolute;
			bottom: 0;
			width: 100%;
			.ui-caption {
				color: $color-transparent-light;
				line-height: 12px;
			}
			div {
				background-color: $color-transparent-dark;
				padding: 4px;
				margin: 8px;
			}
			div:first-child {
				visibility: hidden;
				float: left;
			}
			div:last-child {
				float: right;
			}
		}
		&:hover {
			.media-list-item__image-top-overlay {
				.ui-menu,
				button {
					visibility: visible;
				}
			}
			.media-list-item__play-button {
				cursor: pointer;
				visibility: visible;
			}
			.media-list-item__image-bottom-overlay {
				div:first-child {
					visibility: visible;
				}
			}
		}
	}
	.media-list-item__footer {
		padding: 12px;
		.media-list-item__footer__main {
			display: flex;
			flex-wrap: nowrap;
			.media-list-item__footer__main__text {
				flex-grow: 1;
				min-width: 196px; // 320 - 40 - 12 - 12 - 15 - 15 - 15 - 15
				div {
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
			}
			.media-list-item__footer__main__expand-button {
				.ui-button {
					margin: 0;
				}
			}
		}
		.media-list-item__footer__expanded {
			width: 100%;
			.media-list-item__footer__expanded__actions {
				height: 48px;
				.ui-button {
					float: right;
					margin-right: 0;
				}
			}
		}
	}
}
</style>
